// astro vue will render empty page if import vue from 'vue'
// @ts-ignore
import { createApp } from 'vue/dist/vue.esm-bundler.js'
import { computed, ref, provide, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from '@nanostores/vue'
import {
	exchangeLocalPrice,
	getCurrencySymbolOriginal,
} from '@/assets/ts/currency'
import {
	fetchSettingSupportedCurrencies,
	fetchSettingWarehouses,
	instanceProductSharing,
	instanceBuyandship,
	fetchSettingBuyforyou as fetchSettingBuyforyouApi,
} from '@/assets/ts/apiManagement'
import {
	productSharingData,
	setPostDetails,
	checkExpressCheckoutEnable,
	productSharingPostPerPage,
	getProductSharingUrl,
} from '@/legacy/ts/productSharing'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import useProductSharing from '@/legacy/ts/useProductSharing'
import useHashQuery from '@/legacy/ts/useHashQuery'
import useSnackBar from '@/legacy/ts/useSnackBar'
import BsSnackBar from '@/legacy/components/SnackBar.vue'
import BsDialogAction from '@/legacy/components/DialogAction.vue'
import ProductSharingPostCard from '@/legacy/components/ProductSharingPostCard.vue'
import { fetchPricingCurrencyBuyforyou } from '@/legacy/ts/buyforyou'
import goErrorPage from '@/legacy/ts/goErrorPage'
import hybridPage from '@/lib/hybridPage'
import { setCurrentGaPageName } from '@/lib/utils'
import PageBreadCrumb from '@/components/PageBreadCrumb.vue'
import { getSiteBaseUrl } from '@/lib/siteBaseUrl'
import { bnsRatesStore, fetchBNSRatesOnce } from '@/store/productSharingStore'

hybridPage()

createApp({
	name: 'Product-Sharing-Post',
	components: {
		BsSnackBar,
		BsDialogAction,
		ProductSharingPostCard,
		PageBreadCrumb,
	},
	setup() {
		setCurrentGaPageName(GaGeneralPageName.CommunityPostDetailPage)
		const { postCardTextInfo, postListTextInfo } = window
		const { isShow, messages, dismiss, addMessage } = useSnackBar()
		const { searchParameter } = useHashQuery()
		const { isUserVerified, PSUsername, connectPS, setToken, isHasPSProfile } =
			useProductSharing()

		// work around, since post detail page don't open loop dialog, we provide empty function here
		// provide('postItemClickProvide', () => {
		// 	window.location.href = getProductSharingUrl('post', { id: post.id })
		// })
		// provide('setPostListInfoProvide', () => {})

		provide('addMessage', addMessage)

		const isLoadingPostDetail = ref(true)
		const isShowDialogPostDetail = ref(false)
		const isShowPostContentBlock = ref(false)
		const postDetail: any = ref(null)
		const categoryMap: any = ref(null)
		const warehouseIdMap = ref(null)
		const interestsMap = ref(null)
		const supportedCurrencies = ref(null)
		const buyforyouSupportWarehouse = ref<string[] | null>(null)
		const b4uRates = ref<Record<string, number> | null>(null)
		const bnsRates = useStore(bnsRatesStore)
		const currencySymbolOriginal = ref('')
		const priceLocal = ref('')
		const priceLocalPost = ref('')
		const localCurrencyInfo = ref<Awaited<
			ReturnType<typeof fetchPricingCurrencyBuyforyou>
		> | null>(null)
		const currencyData = ref<Awaited<
			ReturnType<typeof fetchPricingCurrencyBuyforyou>
		> | null>(null)
		const isExpressCheckoutEnable = checkExpressCheckoutEnable()

		const postId = computed(() => {
			if (!import.meta.env.PROD) {
				// use search parameter in local environment
				const id = searchParameter.value.find(query => query.key === 'id')
				return id === undefined ? '' : id.val
			}
			const url = new URL(window.location.href)
			const lastSegment = url.pathname.split('/').filter(Boolean).at(-1)
			return lastSegment || ''
		})

		const locale = computed(() => (import.meta.env.PROD ? '' : 'zh_TW'))
		const isProductSupportExpressCheckout = computed(() => {
			return (
				(postDetail.value &&
					postDetail.value.isExpressCheckout &&
					isExpressCheckoutEnable) ||
				false
			)
		})

		const breadcrumbs = computed(() => {
			return [
				{
					displayName: window.pageContent.breadcrumbs?.home || '',
					categoryId: 'home',
					link: getSiteBaseUrl(),
				},
				{
					displayName: window.pageContent.breadcrumbs?.community || '',
					categoryId: 'community',
					link: getProductSharingUrl('recommended'),
				},
			]
		})

		async function fetchPostDetails() {
			try {
				const {
					data: { data },
				} = await instanceProductSharing.get(`/posts/${postId.value}`)
				if (data.user.username === PSUsername.value) {
					window.location.href = getProductSharingUrl('private-post', {
						id: postId.value,
					})
					return
				}
				postDetail.value = setPostDetails(data)

				createGAEvent(GaGeneralEvent.PageLoad, {
					page_name: GaGeneralPageName.CommunityPostDetailPage,
					page_object_id: `post_${postId.value}`,
					post_id: postId.value,
					product_id: postDetail.value.productSlug,
					is_express_checkout: postDetail.value.isExpressCheckout,
				})
			} catch (error: any) {
				if (error.response && error.response.status === 404) {
					goErrorPage()
				}
				if (error.response && error.response.status === 401) {
					connectPS().then(() => fetchPostDetails())
				}
			}
		}

		function fetchSettingShipmentCategory() {
			if (categoryMap.value) return Promise.resolve()
			return new Promise((resolve, reject) => {
				instanceBuyandship
					.get(`settings/${locale.value}?k[]=shipment_categories`)
					.then(({ data }) => {
						const list = data.shipment_categories.map((v: any) => v[2])
						categoryMap.value = [].concat(...list)
						resolve(true)
					})
					.catch(reject)
			})
		}

		function fetchSettingInterest() {
			return new Promise((resolve, reject) => {
				instanceBuyandship
					.get(`settings/${locale.value}?k[]=shipment_interests`)
					.then(({ data }) => {
						interestsMap.value = data.shipment_interests
						resolve(true)
					})
					.catch(reject)
			})
		}

		async function fetchSettingBuyforyou() {
			try {
				const result = await fetchSettingBuyforyouApi()
				buyforyouSupportWarehouse.value = result.formattedBuyforyouWarehouses
			} catch (error) {
				console.error('Failed to fetch setting: ', error)
				throw error
			}
		}

		function setPostActions(payload: any) {
			if (!payload || !postDetail.value || payload.id !== postDetail.value.id)
				return
			postDetail.value[payload.param] = payload.val
			if (payload.param === 'isLiked') {
				postDetail.value.likeCount = payload.likeCount
			}
		}

		async function setSupportedCurrencies() {
			supportedCurrencies.value = await fetchSettingSupportedCurrencies()
		}

		async function setWarehouseIdMap() {
			warehouseIdMap.value = await fetchSettingWarehouses()
		}

		async function fetchCurrencyData() {
			try {
				currencyData.value = await fetchPricingCurrencyBuyforyou()
			} catch (error) {
				currencyData.value = null
			}
		}

		function setB4URates() {
			if (!currencyData.value) return

			// Transform array to b4uRates object, keeping first occurrence of duplicate keys like APP's behavior
			b4uRates.value = currencyData.value.reduce(
				(acc: { [key: string]: number }, item: any) => {
					if (!acc[item.key]) {
						acc[item.key] = item.rate
					}
					return acc
				},
				{},
			)
		}

		async function setCurrencySymbolOriginal() {
			currencySymbolOriginal.value = getCurrencySymbolOriginal({
				warehouse: postDetail.value.itemInfo.warehouse,
				warehouseIdMap: warehouseIdMap.value,
				supportedCurrencies: supportedCurrencies.value,
			})
		}

		async function setPriceLocal() {
			const priceOriginal = postDetail.value.isExpressCheckout
				? postDetail.value.itemInfo.ecPrice
				: postDetail.value.itemInfo.unitPrice

			const hasB4uRate =
				b4uRates.value?.[postDetail.value.itemInfo.warehouse] != null
			const rates = hasB4uRate ? b4uRates.value : bnsRates.value
			const floatMax = hasB4uRate
				? currencyData.value?.find(
						(item: any) => item.key === postDetail.value.itemInfo.warehouse,
				  )?.currencyDecimal || 0
				: 0

			priceLocal.value = `≈ ${exchangeLocalPrice({
				rates,
				price: priceOriginal,
				warehouse: postDetail.value.itemInfo.warehouse,
				count: 1,
			})}`
			priceLocalPost.value = `≈ ${exchangeLocalPrice({
				rates,
				price: postDetail.value.itemInfo.unitPrice,
				warehouse: postDetail.value.itemInfo.warehouse,
				count: 1,
				floatMax,
			})}`
		}

		function setPricingInfo() {
			localCurrencyInfo.value = currencyData.value
		}

		function handlePopstate() {
			window.location.reload()
		}

		onMounted(async () => {
			if (postId.value) {
				if (productSharingData.isLogin && isUserVerified) {
					await setToken()
				}
				await Promise.all([
					fetchSettingShipmentCategory(),
					fetchSettingInterest(),
					fetchSettingBuyforyou(),
					setWarehouseIdMap(),
					setSupportedCurrencies(),
					fetchCurrencyData(),
					fetchBNSRatesOnce(),
					fetchPostDetails(),
					isHasPSProfile(),
				])

				setB4URates()
				if (isProductSupportExpressCheckout) {
					setPricingInfo()
				}
				await setPriceLocal()
				setCurrencySymbolOriginal()
				isLoadingPostDetail.value = false
			} else {
				goErrorPage()
			}

			window.addEventListener('popstate', handlePopstate)
		})

		onBeforeUnmount(() => {
			window.removeEventListener('popstate', handlePopstate)
		})

		return {
			productSharingPostPerPage,
			postCardTextInfo,
			postListTextInfo,
			isShow,
			messages,
			categoryMap,
			warehouseIdMap,
			interestsMap,
			supportedCurrencies,
			buyforyouSupportWarehouse,
			priceLocal,
			priceLocalPost,
			isShowDialogPostDetail,
			isLoadingPostDetail,
			localCurrencyInfo,
			isShowPostContentBlock,
			postDetail,
			breadcrumbs,
			dismiss,
			setPostActions,
		}
	},
}).mount('#bsMain')
